// Tables.js
import React, { useState, useEffect, useRef } from "react";
import axios from "../../utils/axios";
// import { tableEndpoints } from "../../utils/tableList";
import { useAuth } from "../../context/AuthContext";
import { useTableList } from "../../context/TableListContext";
import "./Tables.css";
import { useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaQuestionCircle, FaTimes, FaInfoCircle, FaColumns, FaPen, FaTrashAlt, FaCheck, FaSearch, FaPlus, FaDownload, FaSortAmountDown, FaChevronDown, FaListUl } from "react-icons/fa";
import logo from "../../assets/logo.png";
import { Chart } from "react-google-charts";
import NotificationModal from './NotificationModal';
import RefundModal from './RefundModal';
import DateRangePicker, { fmtISO, toDateOnly } from './DateRangePicker';

// (removed unused imports)
// Import utility functions
import { 
  handleCreateWrapper,
  handleNotifyWrapper,
  handleDeleteWrapper,
  handleStartEdit,
  handleCommitEdit,
  TRUNCATE_LENGTH,
  getDisplayValue,
  inferColumnType as inferColumnTypeUtil,
  getSortedRows as getSortedRowsUtil,
  getFilteredRows as getFilteredRowsUtil,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  groupTablesByCategory,
  TABLE_PREFILL_CONFIG,
  performAIPrefill,
  fetchAmenityOptions,
  buildForeignKeyMap,
  fetchForeignKeyData,
  fetchTableData,
  fetchAnalyticsColumns,
  computeAnalyticsData,
  parseOrderIds,
  getDateFilterField,
  getFilteredRowsByDateRange,
  parseGuestInfo,
  validateDisplayPriority,
  getFilteredRowsByActiveStatus
} from './TablesUtils';

// Columns shown by default per table (tag -> column names). Tables not listed show every column.
// Users can add or remove columns with the "Columns" button; their choice is remembered in the browser.
const DEFAULT_COLUMNS = {
  Lounge: ['LoungeID', 'LoungeName', 'Country', 'City', 'ActivityStatus', 'EntryFee', 'EntryFeeChildren', 'Currency', 'Capacity', 'DisplayPriority'],
  Airport: ['AirportID', 'IATACode', 'APName', 'CountryCode', 'City'],
  Profiles: ['ID', 'Email', 'FirstName', 'LastName', 'Country', 'City', 'Points', 'ProfileCreatedDate', 'PromoOptIn'],
  Orders: ['OrderID', 'LoungeID', 'UserID', 'FromDateTime', 'Duration', 'ReferenceNo'],
  Transaction: ['TransactionID', 'OrderID', 'PaymentMethod', 'TransactionStatus', 'Amount', 'Currency', 'TimeStamp'],
  Permissions: ['ID', 'UserID', 'FirstName', 'LastName', 'Role', 'Organization', 'Country'],
  WaitList: ['WaitlistID', 'Username', 'Fullname', 'LoungeName', 'FromDateTime', 'TotalGuests', 'Status'],
  Notification: ['ID', 'Username', 'LoungeName', 'Title', 'Category', 'Read_Status', 'Time_Stamp'],
  LoungeVueTransaction: ['TransactionID', 'LoungeID', 'CustomerName', 'Email', 'TotalGuests', 'AmountPaid', 'Currency', 'PaymentStatus', 'CreatedAt'],
};

const COLUMN_PREFS_KEY = 'tv-column-prefs';
const DENSITY_KEY = 'tv-density';
const readStored = (key, fallback) => {
  try {
    const v = window.localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch (e) { return fallback; }
};
const writeStored = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage unavailable */ }
};

const ORDINALS = ['first','second','third','fourth','fifth','sixth','seventh','eighth','ninth','tenth'];
const PRIORITY_OPTIONS = [
  ...ORDINALS.map((o, i) => ({ value: i + 1, label: `Show as ${o}` })),
  { value: null, label: 'Show as random' },
];
const PRIORITY_LABELS = Object.fromEntries(ORDINALS.map((o, i) => [i + 1, `Show as ${o}`]));

const PERMANENT_PRIORITY_MSG =
  "No end date is set, so this lounge will keep this display position until someone changes it manually.\n\nContinue?";

  const TableViewer = () => {
  
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { tableEndpoints, loading: tableListLoading } = useTableList();
  
  // Check if a specific table was selected from navigation
  const selectedTableFromNav = location.state?.selectedTable;
  const [currentTable, setCurrentTable] = useState(null);
  const hasProcessedNavigation = useRef(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [fillableFields, setFillableFields] = useState([]);
  const [keyFields, setKeyFields] = useState([]);
  const [visibleFields, setVisibleFields] = useState([]);
  const [hiddenFields, setHiddenFields] = useState([]);
  const [recordInsertAllowed, setRecordInsertAllowed] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [popupEditMode, setPopupEditMode] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  // Search state
  const [searchText, setSearchText] = useState("");
  const [searchColumn, setSearchColumn] = useState("");

  // Date range filter state (only applies to tables with a mapped date field)
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const dateFilterField = getDateFilterField(currentTable);

  // Active-only filter (Lounge table only)
  const [activeOnly, setActiveOnly] = useState(false);

  // --- Column picker + density (remembered per browser) ---
  const [columnPrefs, setColumnPrefs] = useState(() => readStored(COLUMN_PREFS_KEY, {}));
  const [density, setDensity] = useState(() => readStored(DENSITY_KEY, 'comfortable'));
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [colSearch, setColSearch] = useState('');
  const colMenuRef = useRef(null);

  useEffect(() => { writeStored(COLUMN_PREFS_KEY, columnPrefs); }, [columnPrefs]);
  useEffect(() => { writeStored(DENSITY_KEY, density); }, [density]);
  useEffect(() => { setColMenuOpen(false); setColSearch(''); }, [currentTable?.tag]);
  useEffect(() => {
    if (!colMenuOpen) return undefined;
    const onDown = (e) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setColMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setColMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [colMenuOpen]);

  // Every column the user is allowed to see, in the table's natural order
  const getAllColumns = () =>
    [...keyFields, ...visibleFields.filter((k) => !keyFields.includes(k))]
      .filter((k) => !hiddenFields.includes(k));

  const getDefaultColumns = () => {
    const all = getAllColumns();
    const wanted = DEFAULT_COLUMNS[currentTable?.tag];
    if (!wanted) return all;
    const picked = all.filter((c) => wanted.includes(c));
    return picked.length ? picked : all;
  };

  // Columns currently shown: the user's saved choice, otherwise the table's defaults
  const getShownColumns = () => {
    const all = getAllColumns();
    const saved = columnPrefs[currentTable?.tag];
    if (Array.isArray(saved)) {
      const kept = all.filter((c) => saved.includes(c));
      if (kept.length) return kept;
    }
    return getDefaultColumns();
  };

  const toggleColumn = (col) => {
    const tag = currentTable?.tag;
    const shown = getShownColumns();
    const next = shown.includes(col)
      ? shown.filter((c) => c !== col)
      : getAllColumns().filter((c) => shown.includes(c) || c === col);
    if (!next.length) return;               // always keep at least one column
    setColumnPrefs((p) => ({ ...p, [tag]: next }));
  };
  const showAllColumns = () =>
    setColumnPrefs((p) => ({ ...p, [currentTable?.tag]: getAllColumns() }));
  const resetColumns = () =>
    setColumnPrefs((p) => { const n = { ...p }; delete n[currentTable?.tag]; return n; });


  const getDefaultDateRange = (table) => {
    if (table?.tag === 'Transaction' || table?.tag === 'Orders') {
      const today = toDateOnly(new Date());
      return { from: today, to: today };
    }
    return { from: null, to: null };
  };

  const [exporting, setExporting] = useState(false);

  const handleExportTransactions = async () => {
    try {
      setExporting(true);
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const params = new URLSearchParams();
      if (dateFrom) params.set('start', `${fmtISO(dateFrom)}T00:00:00`);
      if (dateTo) params.set('end', `${fmtISO(dateTo)}T23:59:59`);
      const res = await axios.get(`/api/insights/transactions/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `LoungeAtlas-App_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };
  
  // --- PIE CHART ANALYTICS STATE ---
  const [analyticsColumns, setAnalyticsColumns] = useState([]);
  const [selectedAnalyticsColumn, setSelectedAnalyticsColumn] = useState("");
  const [analyticsData, setAnalyticsData] = useState([]);

  const [refundModal, setRefundModal] = useState({isOpen: false, row: null});
  const [notificationModal, setNotificationModal] = useState({isOpen: false, row: null});
  // Using handleAxiosError from TablesUtils

  // 🔄 Fetch rows and metadata for selected table
  useEffect(() => {
    const go = async () => {
      if (!currentTable || !currentUser) return;
      setLoading(true);
      setError("");
      try {
        const data = await fetchTableData(currentTable, currentUser);
        setRows(data.rows || []);
        setFillableFields(data.fillableFields || []);
        setKeyFields(data.keyFields || []);
                setVisibleFields(data.visibleFields || []);
        setHiddenFields(data.hiddenFields || []);
        setRecordInsertAllowed(!!data.recordInsertAllowed);
      } catch (err) {
        console.error(err);
        setError("Failed to load table or metadata");
      } finally {
        setLoading(false);
      }
    };
    go();
  }, [currentTable, currentUser]);

  const [createError, setCreateError] = useState("");

  const handleCreate = async () => {
    const check = precheckLoungePriority(newRow, null);
    if (!check.ok) { if (check.error) setCreateError(check.error); return; }
    try {
      await handleCreateWrapper(currentTable, newRow, currentUser, {
        onSuccess: (data) => {
          setRows((prev) => [...prev, data]);
          setCreating(false);
          setNewRow({});
          setCreateError(""); // Clear error on success
        },
        onError: (msg) => {
          setCreateError(msg);
        }
      });
    } catch (err) {
      // Error already handled in onError callback
    }
  };

const handleRefund = (row) => {
  setRefundModal({
    isOpen: true,
    row: row
  })
}

const handleNotify = (row) => {
  setNotificationModal({
    isOpen: true,
    row: row
  });
};

// REPLACE the entire handleRefundSubmit with:
const handleRefundSubmit = async (formData) => {
  try {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();

    const transactionId = refundModal.row?.TransactionID;
    const payload = formData.refundType === 'full'
      ? {}
      : { amount: formData.amount };

    const res = await axios.post(
      `/transactions/${transactionId}/refund`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // Update the row in state with the new status
    setRows((prev) =>
      prev.map((r) =>
        r.TransactionID === transactionId
          ? { ...r, TransactionStatus: res.data.TransactionStatus }
          : r
      )
    );

    setRefundModal({ isOpen: false, row: null });
    alert(`Refund processed. New status: ${res.data.TransactionStatus}`);
  } catch (err) {
    const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
    alert(`Refund failed: ${detail}`);
  }
};

// Add new function to handle modal submit
const handleNotificationSubmit = async (formData) => {
  try {
    const result = await handleNotifyWrapper(
      currentTable, 
      notificationModal.row, 
      currentUser,
      formData
    );
    console.log('-------result------------');
    console.log(result);
    console.log('--------------------');
    
    // Close modal on success
    setNotificationModal({ isOpen: false, row: null });
    
    // Optional: Show success message
    alert('Notification sent successfully!');
  } catch (err) {
    console.log(err);
    // Error already handled in onError callback
  }
};

  const handleDelete = async (row) => {
    await handleDeleteWrapper(currentTable, row, keyFields, currentUser, {
      onSuccess: (deletedRow, keyFields) => {
        setRows((prev) => prev.filter((r) => !keyFields.every((k) => r[k] === deletedRow[k])));
      }
    });
  };

  const startEdit = (row) => {
    handleStartEdit(row, keyFields, {
      onStartEdit: (editingId, editData) => {        setEditingId(editingId);
        setEditData(editData);
      }
    });
  };

    // --- Confirm before saving an edit ---
  const [saveConfirm, setSaveConfirm] = useState(null);   // { changes, warning, run }
  const confirmedRef = useRef(false);
  const formatForConfirm = (field, v) => {
    if (v === null || v === undefined || v === '') return '(empty)';
    if (field === 'DisplayPriority') {
      const o = PRIORITY_OPTIONS.find((x) => x.value === v);
      return o ? o.label : String(v);
    }
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
  };
  const sameValue = (a, b) => {
    const norm = (x) => (x === null || x === undefined || x === '') ? '' : String(x).trim();
    return norm(a) === norm(b);
  };
  const requestSave = (run) => {
    const original = rows.find((r) => String(r[keyFields[0]]) === String(editingId)) || {};
    const changes = fillableFields
      .filter((f) => f in editData && !sameValue(editData[f], original[f]))
      .map((f) => ({ field: f, before: formatForConfirm(f, original[f]), after: formatForConfirm(f, editData[f]) }));
    if (!changes.length) {                       // nothing changed: just leave edit mode
      setEditingId(null); setEditData({}); setPopupEditMode(false);
      return;
    }
    let warning = null;
    if (currentTable?.tag === 'Lounge') {
      const { error, needsConfirm } = validateDisplayPriority(editData, original);
      if (error) { alert(error); return; }
      if (needsConfirm) warning = 'No end date is set, so this lounge keeps this display position until someone changes it manually.';
    }
    setSaveConfirm({ changes, warning, run });
  };
  const confirmSave = () => {
    const run = saveConfirm?.run;
    setSaveConfirm(null);
    if (!run) return;
    confirmedRef.current = true;                 // the permanent-priority question was just answered
    try { run(); } finally { confirmedRef.current = false; }
  };
  useEffect(() => {
    if (!saveConfirm) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSaveConfirm(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [saveConfirm]);

  // --- Toolbar helpers: sort menu, active-filter chips, clear all ---
  const sortMenuRef = useRef(null);
  useEffect(() => {
    if (!sortMenuOpen) return undefined;
    const onDown = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) setSortMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setSortMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [sortMenuOpen]);

    // the sort menu floats at a fixed spot, so close it if the page scrolls
  useEffect(() => {
    if (!sortMenuOpen) return undefined;
    const onScroll = (e) => {
      if (e.target instanceof Element && e.target.closest('.tv-sort-pop')) return;
      setSortMenuOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [sortMenuOpen]);

  const fmtChipDate = (d) => (d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '…');
  const getFilterChips = () => {
    const chips = [];
    if (searchText.trim()) chips.push({ key: 'q', label: `Search: “${searchText.trim()}”`, clear: () => setSearchText('') });
    if (searchColumn) chips.push({ key: 'col', label: `In: ${searchColumn}`, clear: () => setSearchColumn('') });
    if (currentTable?.tag === 'Lounge' && activeOnly) chips.push({ key: 'active', label: 'Active lounges only', clear: () => setActiveOnly(false) });
    if (dateFilterField && (dateFrom || dateTo)) {
      chips.push({ key: 'date', label: `${fmtChipDate(dateFrom)} – ${fmtChipDate(dateTo)}`, clear: () => { setDateFrom(null); setDateTo(null); } });
    }
    if (sortColumn) chips.push({ key: 'sort', label: `Sorted by ${sortColumn} ${sortDirection === 'asc' ? '↑' : '↓'}`, clear: () => setSortColumn(null) });
    return chips;
  };
  const clearAllFilters = () => {
    setSearchText(''); setSearchColumn(''); setActiveOnly(false);
    setDateFrom(null); setDateTo(null); setSortColumn(null);
  };

  const commitEdit = async () => {
    const check = precheckLoungePriority(editData, rows.find(r => r[keyFields[0]] === editingId));
    if (!check.ok) { if (check.error) alert(check.error); return; }
    await handleCommitEdit(currentTable, editingId, editData, keyFields, currentUser, {
      onSuccess: (updatedData, editingId, keyFields) => {
        setRows((prev) =>
          prev.map((r) => {
            const idKey = keyFields[0] || Object.keys(r)[0];
            return r[idKey] === editingId ? updatedData : r;
          })
        );
        setEditingId(null);
        setEditData({});
      }
    });
  };

  const renderHeader = () => {
    if (!rows.length) return null;
    const headers = getShownColumns().map((key) => <th key={key}>{key}</th>);
    return headers;
  };

  // Utility for truncating and copying provided by TablesUtils
  const CopyButton = ({ value }) => (
    <button
  className="copy-btn"
  title="Copy"
      onClick={e => {
        e.stopPropagation();
        navigator.clipboard.writeText(value);
      }}
    >Copy</button>
  );

  // Foreign key popup stack for nested referenced rows
  const [fkPopups, setFkPopups] = useState([]); // each item: { loading, data, col, refTable, sourceTable }

  // Helper to build fk map for a given table tag
  const buildFkMapForTable = (tableTag) => {
    const tableMeta = tableEndpoints.find(t => t.tag === tableTag);
    if (!tableMeta) return {};
    return buildForeignKeyMap(tableMeta.table_relations || []);
  };
  const fkMap = buildFkMapForTable(currentTable?.tag);

  // Helpers to leverage table_types for type-aware inputs
  const getColumnMeta = (field) => {
    const types = currentTable?.table_types || {};
    return types[field] || null; // { infoType: string, dataType: string }
  };

  const toDatetimeLocal = (val) => {
    if (!val) return '';
    // Try to parse various date strings
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

    // Floating dropdown menus are positioned from their trigger, so table cells and
  // scroll areas can no longer clip them (this was the "tiny country dropdown")
  const floatingMenuStyle = (ref, minWidth = 260) => {
    const el = ref.current;
    if (!el) return undefined;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom - 16;
    const spaceAbove = r.top - 16;
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(160, Math.min(360, openUp ? spaceAbove : spaceBelow));
    const width = Math.max(r.width, minWidth);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    return openUp
      ? { position: 'fixed', left, bottom: window.innerHeight - r.top + 6, width, maxHeight, marginTop: 0 }
      : { position: 'fixed', left, top: r.bottom + 6, width, maxHeight, marginTop: 0 };
  };

  const renderTypedInput = ({
    field,
    value,
    onChange,
    disabled,
    className,
  }) => {
    const meta = getColumnMeta(field) || {};
    const infoType = (meta.infoType || '').toLowerCase();
    const dataType = (meta.dataType || '').toLowerCase();

    // DisplayPriority -> dropdown; slots held by another lounge are hinted
    if (infoType === 'displaypriority') {
      const currentLoungeId = creating ? null : editingId;
      const heldBy = (p) => rows.find(
        r => r.DisplayPriority === p && String(r.LoungeID) !== String(currentLoungeId)
      );
            const currentValue = (value === '' || value === undefined) ? null : value;
      const currentOpt = PRIORITY_OPTIONS.find(o => o.value === currentValue);
      return (
        <div className="tv-field-group" ref={priorityMenuRef}>
          <button
            type="button"
            className="tv-input tv-select-trigger"
            onClick={() => { if (!disabled) setPriorityMenuOpen(o => !o); }}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={priorityMenuOpen}
          >
            <span>{currentOpt ? currentOpt.label : 'Show as random'}</span>
            <span className="tv-select-caret">▾</span>
          </button>
          {priorityMenuOpen && (
            <div className="tv-select-menu" role="listbox" style={floatingMenuStyle(priorityMenuRef, 420)}>
              {PRIORITY_OPTIONS.map(opt => {
                const owner = opt.value !== null ? heldBy(opt.value) : null;
                const until = owner?.DisplayPriorityEnd ? ` until ${owner.DisplayPriorityEnd}` : '';
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    className={`tv-select-item${opt.value === currentValue ? ' tv-select-item-selected' : ''}`}
                    onClick={() => {
                      onChange(opt.value);
                      setPriorityMenuOpen(false);
                    }}
                  >
                    <span className="tv-select-item-text">
                      {opt.label}{owner ? ` (used by ${owner.LoungeName}${until})` : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Date -> date-only picker
    if (infoType === 'date') {
      return (
        <input
          type="date"
          title={field === 'DisplayPriorityEnd'
            ? 'Optional. Leave empty for no end date: the priority stays until you change it.'
            : field === 'DisplayPriorityStart'
            ? 'Optional. Leave empty to start today.'
            : undefined}
          value={value ? String(value).slice(0, 10) : ''}
          onChange={(e) => onChange(e.target.value)}
          className={className}
          disabled={disabled}
        />
      );
    }

    // TimeZone -> custom dropdown with UTC offsets (usable in edit/popup as well)
    if (infoType === 'timezone') {
      return (
        <div className="tv-field-group" ref={timezoneMenuRef}>
          <button
            type="button"
            className={`tv-input tv-select-trigger${(aiPrefill && aiPrefill[field] !== undefined) ? ' tv-input-prefilled' : ''}`}
            onClick={() => { if (!disabled) setTimezoneMenuOpen(o => !o); }}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={timezoneMenuOpen}
          >
            <span>
              {value || 'Select Time Zone (UTC)'}
            </span>
            <span className="tv-select-caret">▾</span>
          </button>
          {timezoneMenuOpen && (
            <div className="tv-select-menu" role="listbox" style={floatingMenuStyle(timezoneMenuRef, 300)}>
              {timezoneOptions.map(tz => (
                <button
                  key={tz}
                  type="button"
                  className="tv-select-item"
                  onClick={() => {
                    onChange(tz);
                    setTimezoneMenuOpen(false);
                  }}
                >
                  <span className="tv-select-item-text">{tz}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    // AirportID -> custom dropdown (usable in edit/popup as well)
    if (infoType === 'airportid') {
      return (
        <div className="tv-field-group" ref={airportMenuRef}>
          <button
            type="button"
            className={`tv-input tv-select-trigger${(aiPrefill && aiPrefill[field] !== undefined) ? ' tv-input-prefilled' : ''}`}
            onClick={() => { if (!disabled) setAirportMenuOpen(o => !o); }}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={airportMenuOpen}
          >
            <span>
              {(() => {
                if (!value) return 'Select Airport (IATA)';
                const ap = airportOptions.find(a => String(a.AirportID) === String(value));
                return ap ? `${ap.AirportID !== undefined ? ap.AirportID + ' ' : ''}${ap.IATACode || '—'}${ap.APName ? ' - ' + ap.APName : ''}` : 'Select Airport (IATA)';
              })()}
            </span>
            <span className="tv-select-caret">▾</span>
          </button>
          {airportMenuOpen && (
            <div className="tv-select-menu" role="listbox" style={floatingMenuStyle(airportMenuRef, 320)}>
              {airportOptionsLoading && (
                <div className="tv-select-item tv-select-item-muted">Loading…</div>
              )}
              {!airportOptionsLoading && airportOptions.map(ap => (
                <button
                  key={ap.AirportID}
                  type="button"
                  className="tv-select-item"
                  onClick={() => {
                    onChange(String(ap.AirportID));
                    setAirportMenuOpen(false);
                  }}
                >
                  <span className="tv-select-item-text">{`${ap.AirportID} ${ap.IATACode || '—'}${ap.APName ? ` - ${ap.APName}` : ''}`}</span>
                </button>
              ))}
              {!airportOptionsLoading && airportOptions.length === 0 && (
                <div className="tv-select-item tv-select-item-muted">No airports</div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Country -> custom dropdown (usable in edit/popup as well)
    if (infoType === 'country') {
      return (
        <div className="tv-field-group" ref={countryMenuRef}>
          <button
            type="button"
            className={`tv-input tv-select-trigger${(aiPrefill && aiPrefill[field] !== undefined) ? ' tv-input-prefilled' : ''}`}
            onClick={() => { if (!disabled) setCountryMenuOpen(o => !o); }}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={countryMenuOpen}
          >
            <span>
              {(() => {
                if (!value) return 'Select Country (ICAO)';
                const c = countryOptions.find(x => String(x.ICAOCode) === String(value));
                return c ? `${c.ICAOCode}${c.CountryName ? ' - ' + c.CountryName : ''}` : 'Select Country (ICAO)';
              })()}
            </span>
            <span className="tv-select-caret">▾</span>
          </button>
          {countryMenuOpen && (
            <div className="tv-select-menu" role="listbox" style={floatingMenuStyle(countryMenuRef, 300)}>
              {countryOptionsLoading && (
                <div className="tv-select-item tv-select-item-muted">Loading…</div>
              )}
              {!countryOptionsLoading && countryOptions.map(c => (
                <button
                  key={c.ICAOCode}
                  type="button"
                  className="tv-select-item"
                  onClick={() => {
                    onChange(c.ICAOCode);
                    setCountryMenuOpen(false);
                  }}
                >
                  <span className="tv-select-item-text">{c.ICAOCode}{c.CountryName ? ` - ${c.CountryName}` : ''}</span>
                </button>
              ))}
              {!countryOptionsLoading && countryOptions.length === 0 && (
                <div className="tv-select-item tv-select-item-muted">No countries</div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Boolean -> checkbox
    if (infoType === 'boolean' || dataType === 'bool' || dataType === 'boolean') {
      const checked = Boolean(value);
      return (
        <label className="tv-checkbox">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
          />
          <span>{field}</span>
        </label>
      );
    }

    // Number -> number input (int vs float)
    if (infoType === 'number' || dataType === 'int' || dataType === 'float' || dataType === 'number') {
      const step = (dataType === 'int') ? '1' : 'any';
      return (
        <input
          type="number"
          inputMode="decimal"
          step={step}
          placeholder={field}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={className}
          disabled={disabled}
        />
      );
    }

    // DateTime -> simple datetime-local (clean, no overlay)
    if (infoType === 'datetime') {
      return (
        <input
          type="datetime-local"
          value={toDatetimeLocal(value)}
          onChange={(e) => onChange(e.target.value)}
          className={`${className || ''} tv-input-datetime`}
          disabled={disabled}
        />
      );
    }

    // Email
    if (infoType === 'email') {
      return (
        <input
          type="email"
          placeholder={field}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={className}
          disabled={disabled}
        />
      );
    }

    // URL/ImageURL
    if (infoType === 'url' || infoType === 'imageurl') {
      return (
        <input
          type="url"
          placeholder={field}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={className}
          disabled={disabled}
        />
      );
    }

    // Phone
    if (infoType === 'phone') {
      return (
        <input
          type="tel"
          placeholder={field}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={className}
          disabled={disabled}
        />
      );
    }

    // Default text
    return (
      <input
        type="text"
        placeholder={field}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={className}
        disabled={disabled}
      />
    );
  };

  // Handler: fetch referenced row and show popup
  const handleFkInfoClick = async (sourceTableTag, col, value) => {
    const localFkMap = buildFkMapForTable(sourceTableTag);
    const fk = localFkMap[col];
    if (!fk || value === undefined || value === null) return;
    // Push placeholder popup
    const popupIndex = fkPopups.length;
    setFkPopups(prev => ([...prev, { loading: true, data: null, col, refTable: fk.refTable, sourceTable: sourceTableTag }]));
    const data = await fetchForeignKeyData(col, value, localFkMap, tableEndpoints, currentUser);
    setFkPopups(prev => prev.map((p, idx) => idx === popupIndex ? { ...p, loading: false, data: data && !data.error ? data : { error: data?.error || 'Not found' } } : p));
  };

  const priorityStatus = (row) => {
    if (row.DisplayPriority == null) return null;
    const today = new Date().toISOString().slice(0, 10);
    if (row.DisplayPriorityStart && row.DisplayPriorityStart > today) return `Scheduled – starts ${row.DisplayPriorityStart}`;
    return row.DisplayPriorityEnd ? `Active – until ${row.DisplayPriorityEnd}` : 'Active – no end date';
  };

  const renderPriorityValue = (value, row) => {
    const status = priorityStatus(row);
    return (
      <>
        <span>{PRIORITY_LABELS[value] || 'Show as random'}</span>
        {status && <div className="tv-priority-status">{status}</div>}
      </>
    );
  };

  const renderPriorityDate = (k, value, row) => {
    if (value) return <span>{value}</span>;
    if (row.DisplayPriority == null) return <span>—</span>;
    return <span>{k === 'DisplayPriorityEnd' ? 'No end date' : 'Starts immediately'}</span>;
  };

  const precheckLoungePriority = (row, original) => {
    if (currentTable?.tag !== 'Lounge') return { ok: true };
    const { error, needsConfirm } = validateDisplayPriority(row, original);
    if (error) return { ok: false, error };
    if (needsConfirm && !confirmedRef.current && !window.confirm(PERMANENT_PRIORITY_MSG)) return { ok: false, error: null };
    return { ok: true };
  };
  
  // Update renderRowCells to show info icon for FK columns
  const renderRowCells = (row) => {
    const cells = getShownColumns().map((k) => {
      const val = row[k];
      const isLong = typeof val === "string" && val.length > TRUNCATE_LENGTH;
      const isEditing = editingId === row[keyFields[0]];
      const isForeignKey = fkMap[k] !== undefined;
      // Detect boolean columns using table_types
      const meta = getColumnMeta(k) || {};
      const infoType = (meta.infoType || '').toLowerCase();
      const dataType = (meta.dataType || '').toLowerCase();
      const isBoolean = infoType === 'boolean' || dataType === 'bool' || dataType === 'boolean';
      return (
        <td
          key={k}
          className="tv-table-cell"
          title={!isEditing && val !== null && val !== undefined && typeof val !== 'object' && String(val).length > 18 ? String(val) : undefined}
        >
          {isEditing && fillableFields.includes(k) ? (
            renderTypedInput({
              field: k,
              value: isEditing ? (editData[k] ?? '') : val,
              onChange: (newVal) => {
                if (isEditing) setEditData((d) => ({ ...d, [k]: newVal }));
              },
              className: 'tv-input',
              disabled: false,
            })
          ) : (
            <>
              {isBoolean ? (
                <span title={val === true ? 'True' : val === false ? 'False' : ''} style={{fontSize: '1.2em'}}>
                  {val === true ? '✅' : val === false ? '❌' : ''}
                </span>
                ) : currentTable.tag === 'Transaction' && k === 'OrderID' ? null
                : currentTable.tag === 'Lounge' && k === 'DisplayPriority' ? (
                renderPriorityValue(val, row)
                ) : currentTable.tag === 'Lounge' && (k === 'DisplayPriorityStart' || k === 'DisplayPriorityEnd') ? (
                renderPriorityDate(k, val, row)
                ) : currentTable.tag === 'Orders' && k === 'GuestInfo' ? (
                <span className="tv-guest-chip-group">
                  {parseGuestInfo(val).map((g, idx) => {
                    const fullName = `${g.first_name || ''} ${g.last_name || ''}`.trim();
                    return (
                      <span key={g.guest_id ?? idx} className="tv-guest-chip">
                        <span className="tv-guest-chip-name">{fullName || `Guest ${g.guest_id ?? idx + 1}`}</span>
                        {g.age && <span className="tv-guest-chip-age">{g.age}</span>}
                        <span className={`tv-guest-chip-status ${g.confirm ? 'tv-guest-confirmed' : 'tv-guest-pending'}`} title={g.confirm ? 'Confirmed' : 'Not confirmed'}>
                          {g.confirm ? '✓' : '•'}
                        </span>
                      </span>
                    );
                  })}
                  {parseGuestInfo(val).length === 0 && <span className="tv-guest-empty">No guests</span>}
                </span>
              ) : (
                <span title={isLong ? val : undefined}>{getDisplayValue(val)}</span>
              )}
              {isLong && !(currentTable.tag === 'Transaction' && k === 'OrderID') && !(currentTable.tag === 'Orders' && k === 'GuestInfo') && <CopyButton value={val} />}
              {isForeignKey && val !== undefined && val !== null && (
                currentTable.tag === 'Transaction' && k === 'OrderID' ? (
                  <span className="tv-orderid-chip-group">
                    {parseOrderIds(val).map((oid, idx) => (
                      <span key={oid + idx} className="tv-orderid-chip">
                        <span className="tv-orderid-chip-text" title={oid}>{oid.slice(0, 8)}…</span>
                        <FaInfoCircle
                          className="fk-info-icon"
                          title={`Show referenced ${fkMap[k].refTable}`}
                          onClick={e => {
                            e.stopPropagation();
                            handleFkInfoClick(currentTable.tag, k, oid);
                          }}
                        />
                      </span>
                    ))}
                  </span>
                ) : (
                  <FaInfoCircle
                    className="fk-info-icon"
                    title={`Show referenced ${fkMap[k].refTable}`}
                    onClick={e => {
                      e.stopPropagation();
                      handleFkInfoClick(currentTable.tag, k, val);
                    }}
                  />
                )
              )}
            </>
          )}
                </td>
      );
    });

    return cells;
  };

  const handleRowClick = (row) => {
    if (editingId !== null) return; // Don't open popup if editing
    setSelectedRow(row);
    setPopupEditMode(false);
  };

  const startEditInPopup = (row) => {
    const idKey = keyFields[0] || Object.keys(row)[0];
    setEditingId(row[idKey]);
    setEditData({ ...row }); // Prefill editData with current row values
    setPopupEditMode(true);
  };

  const commitEditInPopup = async () => {
    const check = precheckLoungePriority(editData, rows.find(r => r[keyFields[0]] === editingId));
    if (!check.ok) { if (check.error) alert(check.error); return; }
    if (!editingId) {
      alert("Update failed: missing ID");
      return;
    }
    await handleCommitEdit(
      currentTable,
      editingId,
      editData,
      keyFields,
      currentUser,
      {
        onSuccess: (updatedData) => {
          setRows((prev) =>
            prev.map((r) => {
              const idKey = keyFields[0] || Object.keys(r)[0];
              return r[idKey] === editingId ? updatedData : r;
            })
          );
          setEditingId(null);
          setEditData({});
          setPopupEditMode(false);
          setSelectedRow(updatedData);
        }
      }
    );
  };

  const cancelEditInPopup = () => {
    setEditingId(null);
    setEditData({});
    setPopupEditMode(false);
  };

  // Fix: Add closePopup to handle popup close and reset state
  const closePopup = () => {
    setSelectedRow(null);
    setEditingId(null);
    setEditData({});
    setPopupEditMode(false);
  };

  const renderRowActions = (row) => (
    <div className="action-buttons">
      {editingId === row[keyFields[0]] ? (
        <>
          <button className="tv-act tv-act-save" title="Save" aria-label="Save" onClick={() => requestSave(commitEdit)}><FaCheck /></button>
          <button className="tv-act" title="Cancel" aria-label="Cancel" onClick={() => setEditingId(null)}><FaTimes /></button>
        </>
      ) : (
        fillableFields.length > 0 && (
          <>
            <button className="tv-act tv-act-edit" title="Edit" aria-label="Edit" onClick={() => startEdit(row)}><FaPen /></button>
            <button className="tv-act tv-act-delete" title="Delete" aria-label="Delete" onClick={() => handleDelete(row)}><FaTrashAlt /></button>

            {currentTable.tag === 'Transaction' && (
            <button className="tv-act-pill" onClick={() => handleRefund(row)}>Refund</button>
            )}

            {currentTable.tag === 'WaitList' && (
            <button className="tv-act-pill" onClick={() => handleNotify(row)}>Notify</button>
            )}

          </>
        )
      )}
    </div>
  );

  // --- CATEGORY TABS AND GROUPING ---
  const [selectedCategory, setSelectedCategory] = useState('data_management');

  // Group tables by category via utility
  const tablesByCategory = groupTablesByCategory(tableEndpoints);

  // Auto-select a table when category changes or when tableEndpoints are loaded
  useEffect(() => {
    // Don't run if table list is still loading
    if (tableListLoading) {
      
      return;
    }
    
    // If a specific table was selected from navigation, use that
    if (selectedTableFromNav && !hasProcessedNavigation.current) {
      
      hasProcessedNavigation.current = true;
      setCurrentTable(selectedTableFromNav);
      setSelectedCategory(selectedTableFromNav.table_category || 'data_management');
      setRows([]);
      setCreating(false);
      setEditingId(null);
      const navDefaultRange = getDefaultDateRange(selectedTableFromNav);
      setDateFrom(navDefaultRange.from);
      setDateTo(navDefaultRange.to);
      setActiveOnly(false);
      // Clear the navigation state to prevent re-selection on re-render
      navigate(location.pathname, { replace: true });
      return;
    }
    
    // Only auto-select first table if no table is currently selected and not setting from navigation
    if (!currentTable && !hasProcessedNavigation.current) {
      const tables = tablesByCategory[selectedCategory] || [];
      
      if (tables.length > 0) {
        // Always select the first table in the category when category changes
        setCurrentTable(tables[0]);
        setRows([]);
        setCreating(false);
        setEditingId(null);
        const autoDefaultRange = getDefaultDateRange(tables[0]);
        setDateFrom(autoDefaultRange.from);
        setDateTo(autoDefaultRange.to);
        setActiveOnly(false);
      } else {
        console.log('No tables found for category, clearing current table');
        setCurrentTable(null);
        setRows([]);
        setCreating(false);
        setEditingId(null);
      }
    }
    // eslint-disable-next-line
  }, [selectedCategory, tableEndpoints, tableListLoading, selectedTableFromNav, currentTable]);

  // Sort and filter rows using utilities
  const getSortedRows = () => getSortedRowsUtil(rows, sortColumn, sortDirection, (r, c) => inferColumnTypeUtil(r, c));
  const getDisplayRows = () => {
    const searched = getFilteredRowsUtil(getSortedRows(), searchText, searchColumn, keyFields, visibleFields);
    const dateFiltered = getFilteredRowsByDateRange(searched, dateFilterField, dateFrom, dateTo);
    const result = getFilteredRowsByActiveStatus(dateFiltered, currentTable?.tag === 'Lounge' && activeOnly);

    // Demo: float prioritized lounges to the top (only when the user hasn't chosen a manual sort)
    if (currentTable?.tag === 'Lounge' && !sortColumn) {
      const todayISO = fmtISO(new Date());
      const rank = (r) =>
        r.DisplayPriority != null && (!r.DisplayPriorityStart || r.DisplayPriorityStart <= todayISO)
          ? r.DisplayPriority
          : 99;
      return [...result].sort((a, b) => rank(a) - rank(b));
    }
    return result;
  };
  // Compose filtered and sorted rows (handled by utilities above)

  const [sidebarVisible, setSidebarVisible] = useState(true);
  // Hamburger SVG
  // const HamburgerIcon = (
  //   <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
  //     <rect y="6" width="28" height="3.5" rx="1.75" fill="#1572dd"/>
  //     <rect y="12.25" width="28" height="3.5" rx="1.75" fill="#1572dd"/>
  //     <rect y="18.5" width="28" height="3.5" rx="1.75" fill="#1572dd"/>
  //   </svg>
  // );

  const [prefillLoading, setPrefillLoading] = useState(false);

  // Using TABLE_PREFILL_CONFIG imported from TablesUtils

  const [aiPrefill, setAiPrefill] = useState(null);

  // Helper for animated text unveil
  const useAnimatedFieldValues = (fields, values, aiPrefill, duration = 600) => {
    const [animatedValues, setAnimatedValues] = useState(values);
    const animationTimeouts = useRef({});

    useEffect(() => {
      if (!aiPrefill) return;
      // Animate only the fields that changed
      fields.forEach((k, idx) => {
        if (aiPrefill[k] !== undefined && aiPrefill[k] !== values[k]) {
          // Unveil char by char
          const newVal = aiPrefill[k] + "";
          setAnimatedValues((prev) => ({ ...prev, [k]: "" }));
          let i = 0;
          const step = () => {
            setAnimatedValues((prev) => ({ ...prev, [k]: newVal.slice(0, i + 1) }));
            if (i < newVal.length - 1) {
              animationTimeouts.current[k] = setTimeout(step, duration / Math.max(newVal.length, 8));
              i++;
            }
          };
          animationTimeouts.current[k] = setTimeout(step, 80 * idx);
        }
      });
      return () => {
        Object.values(animationTimeouts.current).forEach(clearTimeout);
        animationTimeouts.current = {};
      };
      // eslint-disable-next-line
    }, [aiPrefill]);

    // If values change (user types), update immediately
    useEffect(() => {
      setAnimatedValues(values);
    }, [values]);

    return animatedValues;
  };


  // Amenity options for LoungeAmenities creation
  const [amenityOptions, setAmenityOptions] = useState([]);
  const [amenityDropdownOpen, setAmenityDropdownOpen] = useState(false);
  const amenityDropdownRef = useRef(null);

   // ---- Bulk add amenities (LoungeAmenities) ----
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLounge, setBulkLounge] = useState(null); // selected lounge object
  const [bulkLounges, setBulkLounges] = useState([]);
  const [bulkLoungesLoading, setBulkLoungesLoading] = useState(false);
  const [bulkLoungeOpen, setBulkLoungeOpen] = useState(false);
  const [bulkLoungeQ, setBulkLoungeQ] = useState("");
  const bulkLoungeRef = useRef(null);
  const [bulkChecked, setBulkChecked] = useState([]);
  const [bulkText, setBulkText] = useState("");
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkStatus, setBulkStatus] = useState("Available");
  const [bulkImages, setBulkImages] = useState({}); // lower-case amenity name -> image URL
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkDone, setBulkDone] = useState(null);

  const resetBulk = () => {
    setBulkLounge(null); setBulkLoungeOpen(false); setBulkLoungeQ("");
    setBulkChecked([]); setBulkText(""); setBulkSearch("");
    setBulkStatus("Available"); setBulkImages({}); setBulkError(""); setBulkDone(null);
  };
  const closeBulk = () => { setBulkOpen(false); resetBulk(); };

  // Load amenity names and lounges when the dialog opens
  useEffect(() => {
    if (!bulkOpen) return;
    const go = async () => {
      if (amenityOptions.length === 0) {
        const options = await fetchAmenityOptions(currentUser);
        setAmenityOptions(Array.from(new Set(options)));
      }
      if (bulkLounges.length === 0) {
        try {
          setBulkLoungesLoading(true);
          const { getAuth } = await import('firebase/auth');
          const token = await getAuth().currentUser.getIdToken();
          const res = await axios.get('/lounges/', { headers: { Authorization: `Bearer ${token}` } });
          const arr = Array.isArray(res.data) ? res.data : [];
          arr.sort((a, b) => String(a.LoungeName || '').localeCompare(String(b.LoungeName || '')));
          setBulkLounges(arr);
        } catch (err) {
          console.error('Failed to fetch lounges:', err);
        } finally {
          setBulkLoungesLoading(false);
        }
      }
    };
    go();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkOpen]);

  const loungeLabel = (l) => {
    const place = [l.City, l.Country].filter(Boolean).join(', ');
    return `${l.LoungeName || 'Lounge'}${place ? ' - ' + place : ''}`;
  };

  // Work out what will be added / skipped for the current input
  const getBulkPlan = () => {
    const typed = String(bulkText || "").split(/\n+/).map((line) => {
      const bar = line.indexOf('|');
      return bar === -1
        ? { name: line, url: "" }
        : { name: line.slice(0, bar), url: line.slice(bar + 1) };
    });
    const entries = [...bulkChecked.map((n) => ({ name: n, url: "" })), ...typed];
    const existing = new Set(
      (rows || [])
        .filter((r) => bulkLounge && String(r.LoungeID) === String(bulkLounge.LoungeID))
        .map((r) => String(r.AmenityName || "").trim().toLowerCase())
    );
    const seen = new Set();
    const toAdd = [];
    const alreadyThere = [];
    let repeated = 0;
    entries.forEach((e) => {
      const name = String(e.name || "").replace(/\s+/g, " ").trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (seen.has(key)) { repeated += 1; return; }
      seen.add(key);
      if (existing.has(key)) { alreadyThere.push(name); return; }
      const url = bulkImages[key] !== undefined ? bulkImages[key] : String(e.url || "").trim();
      toAdd.push({ name, key, url });
    });
    return { toAdd, alreadyThere, repeated };
  };

  const toggleBulkName = (name) => {
    setBulkChecked((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };

  const submitBulk = async () => {
    const plan = getBulkPlan();
    if (!bulkLounge) { setBulkError("Choose a lounge first."); return; }
    if (!bulkStatus.trim()) { setBulkError("Enter a status for these amenities."); return; }
    if (plan.toAdd.length === 0) { setBulkError("There is nothing new to add."); return; }
    setBulkBusy(true);
    setBulkError("");
    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${currentTable.path}/bulk`,
        {
          LoungeID: parseInt(bulkLounge.LoungeID, 10),
          AmenityStatus: bulkStatus.trim(),
          Amenities: plan.toAdd.map((a) => ({ AmenityName: a.name, ImageURL: (a.url || "").trim() })),
        },
        { headers }
      );
      const created = res.data?.created || [];
      const skipped = res.data?.skipped || [];
      setRows((prev) => [...prev, ...created]);

      // Best effort: remember any brand-new names in the amenity catalogue
      const known = new Set((amenityOptions || []).map((x) => String(x).toLowerCase()));
      const fresh = created.map((c) => c.AmenityName).filter((n) => !known.has(String(n).toLowerCase()));
      for (const n of fresh) {
        try { await axios.post('/amenity-title/', { AmenityName: n }, { headers }); } catch (e) { /* ignore */ }
      }
      if (fresh.length) setAmenityOptions((prev) => Array.from(new Set([...(prev || []), ...fresh])));

      setBulkDone({ count: created.length, skipped: skipped.length });
    } catch (err) {
      setBulkError(err.response?.data?.detail || err.message || "Bulk add failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  // Airport and Country dropdown options for create form
  const [airportOptions, setAirportOptions] = useState([]); // [{AirportID, IATACode, APName}]
  const [countryOptions, setCountryOptions] = useState([]); // [{ICAOCode, CountryName}]
  const [airportOptionsLoading, setAirportOptionsLoading] = useState(false);
  const [countryOptionsLoading, setCountryOptionsLoading] = useState(false);
  // Custom open-menu state/refs for Airport and Country (closed looks like normal input)
  const [airportMenuOpen, setAirportMenuOpen] = useState(false);
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const [priorityMenuOpen, setPriorityMenuOpen] = useState(false);
  const airportMenuRef = useRef(null);
  const countryMenuRef = useRef(null);
  const priorityMenuRef = useRef(null);
  // TimeZone dropdown state/ref (UTC offsets)
  const [timezoneMenuOpen, setTimezoneMenuOpen] = useState(false);
  const timezoneMenuRef = useRef(null);
  const timezoneOptions = (() => {
    const opts = [];
    for (let i = -12; i <= 14; i++) {
      const sign = i >= 0 ? '+' : '';
      opts.push(`UTC${sign}${i}`);
    }
    return opts;
  })();

  // Close amenity dropdown on outside click
  useEffect(() => {
    if (!amenityDropdownOpen) return;
    const onDocClick = (e) => {
      if (amenityDropdownRef.current && !amenityDropdownRef.current.contains(e.target)) {
        setAmenityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [amenityDropdownOpen]);

  // Close Airport/Country menus on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (airportMenuRef.current && !airportMenuRef.current.contains(e.target)) {
        setAirportMenuOpen(false);
      }
      if (countryMenuRef.current && !countryMenuRef.current.contains(e.target)) {
        setCountryMenuOpen(false);
      }
            if (timezoneMenuRef.current && !timezoneMenuRef.current.contains(e.target)) {
        setTimezoneMenuOpen(false);
      }
      if (priorityMenuRef.current && !priorityMenuRef.current.contains(e.target)) {
        setPriorityMenuOpen(false);
      }
    };
    // Floating menus would drift away from their trigger if the page scrolled, so close them
    const onScroll = (e) => {
      if (e.target instanceof Element && e.target.closest('.tv-select-menu')) return; // scrolling inside a menu is fine
      setAirportMenuOpen(false);
      setCountryMenuOpen(false);
      setTimezoneMenuOpen(false);
      setPriorityMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, []);

  // Fetch AmenityTitle list for AmenityName select in LoungeAmenities
  useEffect(() => {
    // Only fetch if creating LoungeAmenities and AmenityName is a fillable field
    if (
      creating &&
      currentTable &&
      currentTable.tag === 'LoungeAmenities' &&
      fillableFields.includes('AmenityName')
    ) {
      const go = async () => {
        const options = await fetchAmenityOptions(currentUser);
        setAmenityOptions(Array.from(new Set(options)));
      };
      go();
    }
  }, [creating, currentTable, fillableFields, currentUser]);

  // Helper: does the current table have any fillable field with the given infoType?
  const hasFillableInfoType = (type) => {
    if (!currentTable || !fillableFields?.length) return false;
    const tt = currentTable.table_types || {};
    return fillableFields.some((f) => {
      const it = (tt[f]?.infoType || '').toLowerCase();
      return it === String(type).toLowerCase();
    });
  };

  // Fetch Airport list (for any fillable field with infoType AirportID) when creating or editing
  useEffect(() => {
    const shouldFetch = (creating || editingId !== null || popupEditMode) && currentTable && hasFillableInfoType('AirportID');
    if (!shouldFetch) return;
    const go = async () => {
      try {
        setAirportOptionsLoading(true);
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const token = await auth.currentUser.getIdToken();
        const res = await axios.get('/airport/', { headers: { Authorization: `Bearer ${token}` } });
        const arr = Array.isArray(res.data) ? res.data : [];
        setAirportOptions(arr);
      } catch (err) {
        console.error('Failed to fetch airport options:', err);
        setAirportOptions([]);
      } finally {
        setAirportOptionsLoading(false);
      }
    };
    go();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creating, editingId, popupEditMode, currentTable, fillableFields, currentUser]);

  // Fetch Country list (for any fillable field with infoType Country) when creating or editing
  useEffect(() => {
    const shouldFetch = (creating || editingId !== null || popupEditMode) && currentTable && hasFillableInfoType('Country');
    if (!shouldFetch) return;
    const go = async () => {
      try {
        setCountryOptionsLoading(true);
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const token = await auth.currentUser.getIdToken();
        const res = await axios.get('/country/', { headers: { Authorization: `Bearer ${token}` } });
        const arr = Array.isArray(res.data) ? res.data : [];
        setCountryOptions(arr);
      } catch (err) {
        console.error('Failed to fetch country options:', err);
        setCountryOptions([]);
      } finally {
        setCountryOptionsLoading(false);
      }
    };
    go();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creating, editingId, popupEditMode, currentTable, fillableFields, currentUser]);

  // State for custom amenity input
  const [showCustomAmenityInput, setShowCustomAmenityInput] = useState(false);
  const [customAmenityName, setCustomAmenityName] = useState("");
  const [customAmenityTargetField, setCustomAmenityTargetField] = useState(null);
  const animatedNewRow = useAnimatedFieldValues(fillableFields, newRow, aiPrefill);

  const handlePrefillAI = async () => {
    setPrefillLoading(true);
    try {
  const prefill = await performAIPrefill(currentTable, newRow, currentUser);
  setAiPrefill(prefill);
  setNewRow((prev) => ({ ...prev, ...prefill }));
    } catch (err) {
  alert("AI Prefill failed: " + (err.message || 'Unknown error'));
    } finally {
      setPrefillLoading(false);
  setTimeout(() => setAiPrefill(null), 1200);
    }
  };

  const [, setShowHelp] = useState(false);
  const toggleHelp = () => setShowHelp((prev) => !prev);

  // Fetch analytics columns for selected table
  useEffect(() => {
    if (!currentTable || !currentUser) {
      setAnalyticsColumns([]);
      setSelectedAnalyticsColumn("");
      return;
    }
    const go = async () => {
      const cols = await fetchAnalyticsColumns(currentTable, currentUser);
      setAnalyticsColumns(cols);
      setSelectedAnalyticsColumn("");
    };
    go();
  }, [currentTable, currentUser]);

  // Compute analytics data for pie chart
  useEffect(() => {
    const data = computeAnalyticsData(rows, selectedAnalyticsColumn);
    setAnalyticsData(data);
  }, [selectedAnalyticsColumn, rows]);

  return (
    <div className="tv-wrapper">
      
      {/* Move the header outside the horizontally scrollable area and make it fixed */}
      <div
        className="tv-header"
      >
        <div className="tv-header-container">
          <img onClick={() => navigate("/home")} src={logo} alt="Logo" className="sidebar-logo tv-logo" />
          <button
            className="menu-btn tv-menu-btn"
            onClick={() => setSidebarVisible(v => !v)}
            title={sidebarVisible ? "Hide Table List" : "Show Table List"}
            aria-label={sidebarVisible ? "Hide Table List" : "Show Table List"}
          >
            <FaBars size={24} color="white" />
          </button>
          <div className="tv-header-title-container">
            <h1 className="tv-header-title">Lounge Portal Tables</h1>
          </div>
          <button className="class1-btn tv-help-btn" onClick={toggleHelp}>
            <FaQuestionCircle size={20} />
          </button>
        </div>
      </div>

      {/* Move sidebar after header so it is not covered by the fixed header */}
      <aside className={`tv-sidebar tv-sidebar-with-margin${sidebarVisible ? '' : ' hide'}`}>
        

  {/* Table Categories Label */}
  <div className="tv-category-title">Table Categories</div>
  {/* Category Tabs */}
  <div className="tv-category-buttons">
          {CATEGORY_ORDER.map(cat => (
            <button
              key={cat}
              onClick={() => {

                setSelectedCategory(cat);
              }}
              className={`tv-category-button${selectedCategory === cat ? ' active' : ''}`}
            >
              {CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>
        {/* Tables List Label */}
        <div className="tv-tables-title">Tables</div>
        {/* Table List for Selected Category */}
{/* Table List for Selected Category */}
        {tableListLoading ? (
          <div className="tv-loading-message">Loading tables...</div>
        ) : (
          <div className="tv-tables-list">
            {(tablesByCategory[selectedCategory] || []).map((t) => {
              const isActive = currentTable?.path === t.path;
              return (
                <button
                  key={t.path}
                  onClick={() => {
                    if (!isActive) {
                      setCurrentTable(t);
                      setRows([]);
                      setCreating(false);
                      setEditingId(null);
                      const clickDefaultRange = getDefaultDateRange(t);
                      setDateFrom(clickDefaultRange.from);
                      setDateTo(clickDefaultRange.to);
                      setActiveOnly(false);
                    }
                  }}
                  disabled={isActive}
                  className={isActive ? "active disabled" : ""}
                >
                  {t.tag}
                </button>
              );
            })}
          </div>
        )}
        {!tableListLoading && !tableEndpoints.length && (
          <p className="no-tables">No accessible tables available.</p>
        )}
      </aside>



      {/* Add padding to main content to account for fixed header height */}
  <main className="tv-main tv-main-content">
        {/* Indent all main content except the header */}
        <div className="tv-main-inner">
          {!currentTable && !loading && (
            <p className="se">Select a table…</p>
          )}

          {loading && (
            <div className="spinner-wrapper">
              <div className="css-spinner"></div>
            </div>
          )}

          {error && <p className="error">{error}</p>}

          {currentTable && !loading && (
            <>

              {/* Top controls row: search left, record count & analytics right */}
              <div className="tv-top-controls-row" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap', marginBottom: 24 }}>
                                {/* ===== Toolbar: find, filter, sort, view options and actions ===== */}
                <div className="tv-toolbar-card">
                  <div className="tv-tb-row">
                    <div className="tv-tb-search">
                      <FaSearch className="tv-tb-search-icon" aria-hidden="true" />
                      <input
                        type="text"
                        className="tv-tb-input"
                        placeholder={searchColumn ? `Search in ${searchColumn}…` : 'Search all columns…'}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        aria-label="Search"
                      />
                      {searchText && (
                        <button type="button" className="tv-tb-clear" onClick={() => setSearchText('')} aria-label="Clear search" title="Clear search">
                          <FaTimes />
                        </button>
                      )}
                      <select
                        className="tv-tb-select"
                        value={searchColumn}
                        onChange={(e) => setSearchColumn(e.target.value)}
                        aria-label="Search in column"
                        title="Choose which column to search"
                      >
                        <option value="">All columns</option>
                        {getAllColumns().map((col) => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>

                    <div className="tv-tb-actions">
                      {currentTable.tag === 'Transaction' && (
                        <button type="button" className="tv-tb-btn" onClick={handleExportTransactions} disabled={exporting}>
                          <FaDownload /> {exporting ? 'Preparing…' : 'Export Excel'}
                        </button>
                      )}
                      {currentTable.tag === 'LoungeAmenities' && !creating && recordInsertAllowed && (
                        <button type="button" className="tv-tb-btn" onClick={() => { resetBulk(); setBulkOpen(true); }}>
                          <FaListUl /> Bulk add
                        </button>
                      )}
                      {(!creating && recordInsertAllowed && fillableFields.length > 0) && (
                        <button type="button" className="tv-tb-primary" onClick={() => setCreating(true)}>
                          <FaPlus /> Create
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="tv-tb-row tv-tb-tools">
                    <div className="tv-tb-menu" ref={sortMenuRef}>
                      <button
                        type="button"
                        className={`tv-tb-btn${sortColumn ? ' is-on' : ''}`}
                        aria-haspopup="true"
                        aria-expanded={sortMenuOpen}
                        onClick={() => setSortMenuOpen((o) => !o)}
                      >
                        <FaSortAmountDown /> {sortColumn ? `Sort: ${sortColumn} ${sortDirection === 'asc' ? '↑' : '↓'}` : 'Sort'} <FaChevronDown className="tv-tb-caret" />
                      </button>
                      {sortMenuOpen && (
                        <div className="tv-tb-pop tv-sort-pop" role="menu" style={floatingMenuStyle(sortMenuRef, 290)}>
                          <div className="tv-tb-pop-title">Sort by</div>
                          <div className="tv-tb-pop-list">
                            {getAllColumns().map((col) => (
                              <button
                                key={col}
                                type="button"
                                className={`tv-tb-pop-item${sortColumn === col ? ' is-active' : ''}`}
                                onClick={() => {
                                  if (sortColumn === col) setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
                                  else { setSortColumn(col); setSortDirection('asc'); }
                                }}
                              >
                                <span>{col}</span>
                                {sortColumn === col && <span className="tv-tb-pop-dir">{sortDirection === 'asc' ? 'A → Z ↑' : 'Z → A ↓'}</span>}
                              </button>
                            ))}
                          </div>
                          <div className="tv-tb-pop-foot">
                            <span>Click a column again to flip the order</span>
                            <button type="button" onClick={() => { setSortColumn(null); setSortMenuOpen(false); }} disabled={!sortColumn}>Clear sort</button>
                          </div>
                        </div>
                      )}
                    </div>

                    {currentTable.tag === 'Lounge' && (
                      <label className="tv-tb-switch">
                        <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
                        <span className="tv-tb-switch-track" aria-hidden="true"><span className="tv-tb-switch-knob" /></span>
                        <span>Active lounges only</span>
                      </label>
                    )}

                                        <span className="tv-tb-spacer" />

                    {dateFilterField && (
                      <div className="tv-tb-date">
                        <DateRangePicker
                          value={{ from: dateFrom, to: dateTo }}
                          onChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
                          label="Date range"
                        />
                      </div>
                    )}
                  </div>

                  <div className="tv-tb-summary" role="status" aria-live="polite">
                    <span className="tv-tb-count"><strong>{getDisplayRows().length}</strong> {getDisplayRows().length === 1 ? 'record' : 'records'}</span>
                    {getFilterChips().map((c) => (
                      <span key={c.key} className="tv-chip">
                        {c.label}
                        <button type="button" className="tv-chip-x" onClick={c.clear} aria-label={`Remove filter: ${c.label}`}><FaTimes /></button>
                      </span>
                    ))}
                    {getFilterChips().length > 0 && (
                      <button type="button" className="tv-tb-clearall" onClick={clearAllFilters}>Clear all</button>
                    )}
                  </div>
                </div>

                {/* Right-hand column now only holds the analytics chart */}
                <div className="tv-insight-wrap">
                  {/* --- ANALYTICS PIE CHART --- */}
                  {analyticsColumns.length > 0 && (
                                        <div
                      className="tv-analytics-container"
                      style={{
                        marginTop: 0,
                        marginBottom: 0,
                        padding: 12,
                        minWidth: 0,
                        maxWidth: 504,
                        width: '120%',
                        height: selectedAnalyticsColumn && analyticsData.length > 0 ? '340px' : '56px',
                        transition: 'all 0.2s',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        overflow: 'hidden'
                      }}
                    >
                      <div className="tv-analytics-controls tv-flex-column-gap">
                        <div className="tv-analytics-control-row tv-flex-row-gap-8">
                          <span className="tv-analytics-label">Select Attribute:</span>
                          <select
                            value={selectedAnalyticsColumn}
                            onChange={e => setSelectedAnalyticsColumn(e.target.value)}
                            className="tv-analytics-select"
                          >
                            <option value="">No Attribute</option>
                            {analyticsColumns.map(col => (
                              <option key={col} value={col}>{col}</option>
                            ))}
                          </select>
                        </div>
                        {selectedAnalyticsColumn && analyticsData.length > 0 ? (() => {
                          const googlePieData = [
                            [selectedAnalyticsColumn, 'Count'],
                            ...analyticsData.map(d => [d.name, d.value])
                          ];
                          const pieOptions = {
                            legend: { position: 'right', textStyle: { fontSize: 11 } },
                            chartArea: { width: '70%', height: '80%' },
                            pieHole: 0,
                            fontName: 'Inter, Arial, sans-serif',
                            colors: ["#1572dd", "#0e265a", "#f7b731", "#eb3b5a", "#20bf6b", "#8854d0", "#fd9644", "#4b6584"],
                            sliceVisibilityThreshold: 0
                          };
                          return (
                            <div className="tv-chart-container" style={{ padding: 0, minHeight: 220, height: 320 }}>
                              <Chart
                                chartType="PieChart"
                                data={googlePieData}
                                options={pieOptions}
                                width={'100%'}
                                height={'280px'}
                              />
                            </div>
                          );
                        })() : null}
                      </div>
                    </div>
                  )}
                  {/* --- END ANALYTICS PIE CHART --- */}
                </div>
              </div>

            

            {creating ? (
              <div className="tv-create tv-create-form-container">
                {/* Error message row */}
                {createError && (
                  <div className="tv-error-banner">
                    <span>
                      {(() => {
                        const fastapiUnique = createError.match(/([A-Za-z]+) with unique values for \[([^\]]+)\] already exists\./i);
                        if (fastapiUnique) {
                          const table = fastapiUnique[1];
                          const cols = fastapiUnique[2].split(',').map(s => s.replace(/['"\s]/g, ''));
                          return (
                            <>
                              <span className="tv-error-bold">Duplicate entry:</span> {table} with the same {cols.join(', ')} already exists.
                            </>
                          );
                        }
                        const match = createError.match(/Unique constraint violated for columns: ([^\n]+)/i);
                        if (match) {
                          const cols = match[1].split(',').map(s => s.trim());
                          return (
                            <>
                              <span className="tv-error-bold">Duplicate entry:</span> The following field{cols.length > 1 ? 's are' : ' is'} already exist{cols.length > 1 ? '' : 's'} in another row:
                              {cols.map((col, i) => (
                                <span key={col} className="tv-duplicate-field">{col}{i < cols.length-1 ? ', ' : ''}</span>
                              ))}
                            </>
                          );
                        }
                        return createError;
                      })()}
                    </span>
                    <button
                      onClick={() => setCreateError("")}
                      className="tv-error-close"
                      aria-label="Dismiss error"
                    >×</button>
                  </div>
                )}
                {/* Data entry row */}
                <div className="tv-form-fields-container">
                  {/* AI Prefill Required Fields & Button Stylish Container - now stacked vertically */}
                  {currentTable && TABLE_PREFILL_CONFIG[currentTable?.path?.replace(/^\//, "")] && (
                    <div className="ai-prefill-fields-container tv-ai-prefill-container">
                      <div className="ai-prefill-fields-row">
                        <label className="ai-prefill-label">
                          {TABLE_PREFILL_CONFIG[currentTable?.path?.replace(/^\//, "")].triggerField}
                        </label>
                        <input
                          type="text"
                          className={
                            `ai-prefill-required-input${TABLE_PREFILL_CONFIG[currentTable?.path?.replace(/^\//, "")].triggerField === "CountryName" ? " country-input" : ""}`
                          }
                          value={newRow[TABLE_PREFILL_CONFIG[currentTable?.path?.replace(/^\//, "")].triggerField] || ""}
                          onChange={e => setNewRow(nr => ({ ...nr, [TABLE_PREFILL_CONFIG[currentTable?.path?.replace(/^\//, "")].triggerField]: e.target.value }))}
                          placeholder={TABLE_PREFILL_CONFIG[currentTable?.path?.replace(/^\//, "")].triggerField}
                          disabled={prefillLoading}
                          maxLength={TABLE_PREFILL_CONFIG[currentTable?.path?.replace(/^\//, "")].triggerField === "CountryName" ? 64 : (TABLE_PREFILL_CONFIG[currentTable?.path?.replace(/^\//, "")].triggerLength || 64)}
                        />
                      </div>
                      <div className="ai-prefill-btn-row">
                        {prefillLoading ? (
                          <div className="tv-loading-spinner-container">
                            {/* Replace TailSpin with CSS spinner */}
                            <div className="css-spinner"></div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="ai-prefill-btn"
                            onClick={handlePrefillAI}
                            disabled={
                              !TABLE_PREFILL_CONFIG[currentTable?.path?.replace(/^\//, "")].isEnabled(newRow) || prefillLoading
                            }
                          >
                            {TABLE_PREFILL_CONFIG[currentTable?.path?.replace(/^\//, "")].buttonLabel(newRow)}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {/* The rest of the fillable fields, below the AI container */}
                  <div className="tv-input-fields-container">
                    {fillableFields.filter(k => k !== (TABLE_PREFILL_CONFIG[currentTable?.path?.replace(/^\/*/, "")]?.triggerField)).map((k) => (
                      currentTable && currentTable.tag === 'LoungeAmenities' && k === 'AmenityName' ? (
                        <div key={k} className="tv-amenity-block">
                          <div className="tv-field-group tv-flex-row-gap-8" ref={amenityDropdownRef}>
                            <div className="tv-dropdown">
                              <label className="tv-form-label">{k}</label>
                              <button
                                type="button"
                                className="tv-dropdown-trigger"
                                disabled={prefillLoading}
                                onClick={() => setAmenityDropdownOpen(o => !o)}
                                aria-haspopup="listbox"
                                aria-expanded={amenityDropdownOpen}
                              >
                                <span className="tv-dropdown-label">{animatedNewRow[k] || 'Select Amenity'}</span>
                                <span className="tv-dropdown-caret">▾</span>
                              </button>
                              {amenityDropdownOpen && (
                                <div className="tv-dropdown-menu" role="listbox">
                                  {amenityOptions.map(option => (
                                    <div key={option} className="tv-dropdown-item">
                                      <button
                                        type="button"
                                        className="tv-dropdown-item-text"
                                        onClick={() => {
                                          setNewRow(nr => ({ ...nr, [k]: option }));
                                          setShowCustomAmenityInput(false);
                                          setAmenityDropdownOpen(false);
                                        }}
                                      >
                                        {option}
                                      </button>
                                      <button
                                        type="button"
                                        className="tv-dropdown-remove"
                                        title={`Remove '${option}' from Amenity List`}
                                        disabled={prefillLoading}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (!window.confirm(`Remove '${option}' from Amenity List?`)) return;
                                          try {
                                            const { getAuth } = await import('firebase/auth');
                                            const auth = getAuth();
                                            const token = await auth.currentUser.getIdToken();
                                            const res = await axios.get('/amenity-title/', { headers: { Authorization: `Bearer ${token}` } });
                                            const found = (res.data || []).find(a => a.AmenityName === option);
                                            if (!found) return alert('Amenity not found in DB.');
                                            await axios.delete(`/amenity-title/${found.ID}`, { headers: { Authorization: `Bearer ${token}` } });
                                            setAmenityOptions(prev => prev.filter(o => o !== option));
                                            setNewRow(nr => nr[k] === option ? { ...nr, [k]: '' } : nr);
                                          } catch (err) {
                                            alert('Failed to remove amenity: ' + (err.response?.data?.detail || err.message));
                                          }
                                        }}
                                      >
                                        &minus;
                                      </button>
                                    </div>
                                  ))}
                                  <div className="tv-dropdown-divider" />
                                  <button
                                    type="button"
                                    className="tv-dropdown-item tv-dropdown-item-text"
                                    onClick={() => {
                                      setCustomAmenityTargetField(k);
                                      setCustomAmenityName('');
                                      setShowCustomAmenityInput(true);
                                      setAmenityDropdownOpen(false);
                                    }}
                                  >
                                    Add Custom…
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Modal replaces inline custom input */}
                        </div>
                      ) : (() => {
                        const meta = getColumnMeta(k) || {};
                        const infoType = (meta.infoType || '').toLowerCase();
                        // AirportID dropdown (options from /airport/ using IATACode, sets AirportID value)
                        if (infoType === 'airportid') {
                          return (
                            <div key={k} className="tv-form-field" ref={airportMenuRef}>
                              <label className="tv-form-label">{k}</label>
                              <button
                                type="button"
                                className={`tv-input tv-select-trigger${aiPrefill && aiPrefill[k] !== undefined ? ' tv-input-prefilled' : ''}`}
                                onClick={() => { if (!prefillLoading) setAirportMenuOpen(o => !o); }}
                                disabled={prefillLoading}
                                aria-haspopup="listbox"
                                aria-expanded={airportMenuOpen}
                              >
                                <span>
                                  {(() => {
                                    const val = animatedNewRow[k];
                                    if (!val) return 'Select Airport (IATA)';
                                    const ap = airportOptions.find(a => String(a.AirportID) === String(val));
                                    return ap ? `${ap.AirportID !== undefined ? ap.AirportID + ' ' : ''}${ap.IATACode || '—'}${ap.APName ? ' - ' + ap.APName : ''}` : 'Select Airport (IATA)';
                                  })()}
                                </span>
                                <span className="tv-select-caret">▾</span>
                              </button>
                              {airportMenuOpen && (
                                <div className="tv-select-menu" role="listbox">
                                  {airportOptionsLoading && (
                                    <div className="tv-select-item tv-select-item-muted">Loading…</div>
                                  )}
                                  {!airportOptionsLoading && airportOptions.map(ap => (
                                    <button
                                      key={ap.AirportID}
                                      type="button"
                                      className="tv-select-item"
                                      onClick={() => {
                                        setNewRow(nr => ({ ...nr, [k]: String(ap.AirportID) }));
                                        setAirportMenuOpen(false);
                                      }}
                                    >
                                      <span className="tv-select-item-text">{`${ap.AirportID} ${ap.IATACode || '—'}${ap.APName ? ` - ${ap.APName}` : ''}`}</span>
                                    </button>
                                  ))}
                                  {!airportOptionsLoading && airportOptions.length === 0 && (
                                    <div className="tv-select-item tv-select-item-muted">No airports</div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }
                        // Country dropdown (ICAOCode values from /country/)
                        if (infoType === 'country') {
                          return (
                            <div key={k} className="tv-form-field" ref={countryMenuRef}>
                              <label className="tv-form-label">{k}</label>
                              <button
                                type="button"
                                className={`tv-input tv-select-trigger${aiPrefill && aiPrefill[k] !== undefined ? ' tv-input-prefilled' : ''}`}
                                onClick={() => { if (!prefillLoading) setCountryMenuOpen(o => !o); }}
                                disabled={prefillLoading}
                                aria-haspopup="listbox"
                                aria-expanded={countryMenuOpen}
                              >
                                <span>
                                  {(() => {
                                    const val = animatedNewRow[k];
                                    if (!val) return 'Select Country (ICAO)';
                                    const c = countryOptions.find(x => String(x.ICAOCode) === String(val));
                                    return c ? `${c.ICAOCode}${c.CountryName ? ' - ' + c.CountryName : ''}` : 'Select Country (ICAO)';
                                  })()}
                                </span>
                                <span className="tv-select-caret">▾</span>
                              </button>
                              {countryMenuOpen && (
                                <div className="tv-select-menu" role="listbox">
                                  {countryOptionsLoading && (
                                    <div className="tv-select-item tv-select-item-muted">Loading…</div>
                                  )}
                                  {!countryOptionsLoading && countryOptions.map(c => (
                                    <button
                                      key={c.ICAOCode}
                                      type="button"
                                      className="tv-select-item"
                                      onClick={() => {
                                        setNewRow(nr => ({ ...nr, [k]: c.ICAOCode }));
                                        setCountryMenuOpen(false);
                                      }}
                                    >
                                      <span className="tv-select-item-text">{c.ICAOCode}{c.CountryName ? ` - ${c.CountryName}` : ''}</span>
                                    </button>
                                  ))}
                                  {!countryOptionsLoading && countryOptions.length === 0 && (
                                    <div className="tv-select-item tv-select-item-muted">No countries</div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }
                        // Default typed input
                        return (
                          <div key={k} className="tv-form-field">
                            <label className="tv-form-label">{k}</label>
                            {renderTypedInput({
                              field: k,
                              value: animatedNewRow[k] ?? '',
                              onChange: (v) => setNewRow((nr) => ({ ...nr, [k]: v })),
                              className: `tv-input${aiPrefill && aiPrefill[k] !== undefined ? ' tv-input-prefilled' : ''}`,
                              disabled: prefillLoading,
                            })}
                          </div>
                        );
                      })()
                    ))}
                  </div>
                </div>
                <div className="tv-form-actions">
                  {!prefillLoading && <button onClick={handleCreate}>Save</button>}
                  {!prefillLoading && <button onClick={() => { setCreating(false); setCreateError(""); }}>Cancel</button>}
                </div>
              </div>
            ) : (
              recordInsertAllowed && fillableFields.length > 0 && (
                // <button onClick={() => setCreating(true)}>+ Create</button>
                <div></div>
              )
            )}

            {rows.length > 0 && (
              <div className="tv-grid-toolbar">
                <span className="tv-grid-count">
                  Showing {getShownColumns().length} of {getAllColumns().length} columns
                </span>
                <div className="tv-grid-toolbar-right">
                  <div className="tv-seg" role="group" aria-label="Row density">
                    <button
                      type="button"
                      className={density === 'comfortable' ? 'active' : ''}
                      onClick={() => setDensity('comfortable')}
                    >Comfortable</button>
                    <button
                      type="button"
                      className={density === 'compact' ? 'active' : ''}
                      onClick={() => setDensity('compact')}
                    >Compact</button>
                  </div>
                  <div className="tv-colpicker" ref={colMenuRef}>
                    <button
                      type="button"
                      className="tv-toolbar-btn"
                      aria-haspopup="true"
                      aria-expanded={colMenuOpen}
                      onClick={() => setColMenuOpen((o) => !o)}
                    >
                      <FaColumns /> Columns
                    </button>
                    {colMenuOpen && (
                      <div className="tv-colmenu" role="menu">
                        <input
                          type="text"
                          className="tv-colmenu-search"
                          placeholder="Find a column…"
                          value={colSearch}
                          onChange={(e) => setColSearch(e.target.value)}
                          autoFocus
                        />
                        <div className="tv-colmenu-list">
                          {getAllColumns()
                            .filter((c) => c.toLowerCase().includes(colSearch.trim().toLowerCase()))
                            .map((c) => (
                              <label key={c} className="tv-colmenu-item">
                                <input
                                  type="checkbox"
                                  checked={getShownColumns().includes(c)}
                                  onChange={() => toggleColumn(c)}
                                />
                                <span>{c}</span>
                              </label>
                            ))}
                        </div>
                        <div className="tv-colmenu-footer">
                          <button type="button" onClick={showAllColumns}>Show all</button>
                          <button
                            type="button"
                            onClick={resetColumns}
                            disabled={!Array.isArray(columnPrefs[currentTable?.tag])}
                          >Reset to default</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {getDisplayRows().length > 0 ? (
              <div className={`tv-grid${density === 'compact' ? ' tv-grid-compact' : ''}`}>
              <table className="tv-grid-table">
                <thead>
                  <tr>
                    {renderHeader()}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
          {getDisplayRows().map((row) => (
                    <tr
                      key={keyFields.map((k) => row[k]).join("-") || row.id || Math.random()}
                      onClick={() => handleRowClick(row)}
            className="tv-row-clickable"
                    >
                      {renderRowCells(row)}
                      <td onClick={e => e.stopPropagation()}>
                        {renderRowActions(row)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                            </table>
              </div>
            ) : (
              !loading && <p>No data.</p>
            )}
          </>
        )}
        </div>
      </main>

      {/* Confirm-before-save dialog */}
      {saveConfirm && (
        <div className="tv-confirm-overlay" onMouseDown={() => setSaveConfirm(null)}>
          <div
            className="tv-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tv-confirm-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="tv-confirm-icon" aria-hidden="true"><FaPen /></div>
            <h3 id="tv-confirm-title">Save these changes?</h3>
            <p className="tv-confirm-sub">
              {currentTable?.tag} · {keyFields[0]} {editingId}
            </p>
            <ul className="tv-confirm-changes">
              {saveConfirm.changes.map((c) => (
                <li key={c.field}>
                  <span className="tv-confirm-field">{c.field}</span>
                  <span className="tv-confirm-values">
                    <span className="tv-confirm-before">{c.before}</span>
                    <span className="tv-confirm-arrow" aria-hidden="true">→</span>
                    <span className="tv-confirm-after">{c.after}</span>
                  </span>
                </li>
              ))}
            </ul>
            {saveConfirm.warning && <div className="tv-confirm-warning">{saveConfirm.warning}</div>}
            <div className="tv-confirm-actions">
              <button type="button" className="tv-confirm-cancel" onClick={() => setSaveConfirm(null)}>Keep editing</button>
              <button type="button" className="tv-confirm-ok" autoFocus onClick={confirmSave}>Save changes</button>
            </div>
          </div>
        </div>
      )}

            {/* Refund + Notification pop-ups: one copy each for the whole page (not one per row) */}
      <RefundModal
        isOpen={refundModal.isOpen}
        onClose={() => setRefundModal({ isOpen: false, row: null })}
        onSubmit={handleRefundSubmit}
        orderID={refundModal.row?.OrderID || ''}
        paymentMethod={refundModal.row?.PaymentMethod || ''}
        transactionStatus={refundModal.row?.TransactionStatus || ''}
        amount={refundModal.row?.Amount || ''}
        currency={refundModal.row?.Currency || ''}
        cardBrand={refundModal.row?.CardBrand || ''}
        stripePaymentIntent={refundModal.row?.StripePaymentIntentID || ''}
      />
      <NotificationModal
        isOpen={notificationModal.isOpen}
        onClose={() => setNotificationModal({ isOpen: false, row: null })}
        onSubmit={handleNotificationSubmit}
        username={notificationModal.row?.Username || ''}
        device_token={notificationModal.row?.Device_Token || ''}
        loungeID={notificationModal.row?.LoungeID || ''}
        loungeName={notificationModal.row?.LoungeName || ''}
        totalGuests={notificationModal.row?.TotalGuests || ''}
        phoneNumber={notificationModal.row?.PhoneNumber || ''}
        communication={notificationModal.row?.Communication || ''}
      />

      {/* Row Details Popup */}
      {selectedRow && (
        <div className="tv-popup-overlay" onClick={closePopup}>
          <div className="tv-popup" onClick={e => e.stopPropagation()}>
            <h3 className="tv-rd-title">
              <span className="tv-rd-heading">
                Row details
                <span className="tv-rd-sub">
                  {currentTable?.tag} · {keyFields[0]} {selectedRow[keyFields[0]]}
                  {popupEditMode && <span className="tv-rd-editing">Editing</span>}
                </span>
              </span>
              <button type="button" className="tv-rd-x" onClick={closePopup} aria-label="Close"><FaTimes /></button>
            </h3>
            <table className="tv-popup-table">
              <tbody>
                {Object.entries(selectedRow).map(([k, v]) => {
                  const isLong = typeof v === "string" && v.length > TRUNCATE_LENGTH;
                  const isForeignKey = fkMap[k] !== undefined && v !== undefined && v !== null;
                  // Detect boolean columns using table_types
                  const meta = getColumnMeta(k) || {};
                  const infoType = (meta.infoType || '').toLowerCase();
                  const dataType = (meta.dataType || '').toLowerCase();
                  const isBoolean = infoType === 'boolean' || dataType === 'bool' || dataType === 'boolean';
                  return (
                    <tr key={k}>
                      <td className="tv-cell-bold">{k}</td>
                      <td className="tv-popup-ellipsis">
                        {popupEditMode && fillableFields.includes(k) ? (
                          renderTypedInput({
                            field: k,
                            value: editData[k] ?? '',
                            onChange: (v) => setEditData((d) => ({ ...d, [k]: v })),
                            className: 'tv-input',
                            disabled: false,
                          })
                        ) : (
                          <>
                            {isBoolean ? (
                              <span className={`tv-pill ${v === true ? 'tv-pill-yes' : 'tv-pill-no'}`}>
                                {v === true ? 'Yes' : v === false ? 'No' : '—'}
                              </span>
                            ) : currentTable.tag === 'Transaction' && k === 'OrderID' ? null
                              : currentTable.tag === 'Lounge' && k === 'DisplayPriority' ? (
                              renderPriorityValue(v, selectedRow)
                            ) : currentTable.tag === 'Lounge' && (k === 'DisplayPriorityStart' || k === 'DisplayPriorityEnd') ? (
                              renderPriorityDate(k, v, selectedRow)
                            ) : currentTable.tag === 'Orders' && k === 'GuestInfo' ? (
                              <span className="tv-guest-chip-group">
                                {parseGuestInfo(v).map((g, idx) => {
                                  const fullName = `${g.first_name || ''} ${g.last_name || ''}`.trim();
                                  return (
                                    <span key={g.guest_id ?? idx} className="tv-guest-chip">
                                      <span className="tv-guest-chip-name">{fullName || `Guest ${g.guest_id ?? idx + 1}`}</span>
                                      {g.age && <span className="tv-guest-chip-age">{g.age}</span>}
                                      <span className={`tv-guest-chip-status ${g.confirm ? 'tv-guest-confirmed' : 'tv-guest-pending'}`} title={g.confirm ? 'Confirmed' : 'Not confirmed'}>
                                        {g.confirm ? '✓' : '•'}
                                      </span>
                                    </span>
                                  );
                                })}
                                {parseGuestInfo(v).length === 0 && <span className="tv-guest-empty">No guests</span>}
                              </span>
                            ) : (
                              <span title={isLong ? v : undefined}>{getDisplayValue(v)}</span>
                            )}
                            {isLong && !(currentTable.tag === 'Transaction' && k === 'OrderID') && !(currentTable.tag === 'Orders' && k === 'GuestInfo') && <CopyButton value={v} />}
                            {isForeignKey && (
                              currentTable.tag === 'Transaction' && k === 'OrderID' ? (
                                <span className="tv-orderid-chip-group">
                                  {parseOrderIds(v).map((oid, idx) => (
                                    <span key={oid + idx} className="tv-orderid-chip">
                                      <span className="tv-orderid-chip-text" title={oid}>{oid.slice(0, 8)}…</span>
                                      <FaInfoCircle
                                        className="fk-info-icon"
                                        title={`Show referenced ${fkMap[k].refTable}`}
                                        onClick={e => {
                                          e.stopPropagation();
                                          handleFkInfoClick(currentTable.tag, k, oid);
                                        }}
                                      />
                                    </span>
                                  ))}
                                </span>
                              ) : (
                                <FaInfoCircle
                                  className="fk-info-icon"
                                  title={`Show referenced ${fkMap[k].refTable}`}
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleFkInfoClick(currentTable.tag, k, v);
                                  }}
                                />
                              )
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="tv-popup-actions">
              {popupEditMode ? (
                <>
                  <button className="tv-rd-primary" onClick={() => requestSave(commitEditInPopup)}>Save changes</button>
                  <button className="tv-rd-secondary" onClick={cancelEditInPopup}>Cancel</button>
                </>
              ) : (
                <>
                  <button className="tv-rd-secondary" onClick={closePopup}>Close</button>
                  <button className="tv-rd-primary" onClick={() => startEditInPopup(selectedRow)}><FaPen /> Edit row</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Foreign Key Info Popups (stacked) */}
      {fkPopups.map((popup, idx) => {
        const localFkMap = buildFkMapForTable(popup.refTable);
        return (
          <div
            key={idx}
            className="tv-popup-overlay"
            style={{ zIndex: 2100 + idx }}
            onClick={() => {
              // Close only this popup and any above it
              setFkPopups(prev => prev.filter((_, i) => i < idx));
            }}
          >
            <div className="tv-popup" onClick={e => e.stopPropagation()} style={{ zIndex: 2110 + idx }}>
              <h3 className="tv-rd-title">
                <span className="tv-rd-heading">
                  Referenced row
                  <span className="tv-rd-sub">{popup.refTable}</span>
                </span>
                <button
                  type="button"
                  className="tv-rd-x"
                  onClick={() => setFkPopups(prev => prev.filter((_, i) => i !== idx))}
                  aria-label="Close"
                ><FaTimes /></button>
              </h3>
              {popup.loading ? (
                <div className="css-spinner"></div>
              ) : popup.data && !popup.data.error ? (
                <table className="tv-popup-table">
                  <tbody>
                    {Object.entries(popup.data).map(([k, v]) => {
                      const isFk = localFkMap[k] !== undefined && v !== undefined && v !== null;
                      return (
                        <tr key={k}>
                          <td className="tv-cell-bold">{k}</td>
                          <td className="tv-popup-ellipsis">
                            <span>{String(v)}</span>
                            {isFk && (
                              <FaInfoCircle
                                className="fk-info-icon"
                                title={`Show referenced ${localFkMap[k].refTable}`}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleFkInfoClick(popup.refTable, k, v);
                                }}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="tv-text-dark-red">Error: {popup.data?.error || "Not found"}</div>
              )}
              <div className="tv-popup-actions">
                <button className="tv-rd-secondary" onClick={() => setFkPopups(prev => prev.filter((_, i) => i !== idx))}>Close</button>
              </div>
            </div>
          </div>
        );
      })}

           {/* Bulk add amenities */}
      {bulkOpen && currentTable && currentTable.tag === 'LoungeAmenities' && (() => {
        const plan = getBulkPlan();
        const q = bulkSearch.trim().toLowerCase();
        const visibleOptions = (amenityOptions || []).filter((o) => !q || String(o).toLowerCase().includes(q));
        const statusChoices = Array.from(new Set((rows || []).map((r) => r.AmenityStatus).filter(Boolean)));
        const lq = bulkLoungeQ.trim().toLowerCase();
        const visibleLounges = bulkLounges.filter((l) => !lq || `${loungeLabel(l)} ${l.LoungeID}`.toLowerCase().includes(lq));
        return (
          <div className="tv-bulk-overlay" onClick={() => { if (!bulkBusy) closeBulk(); }}>
            <div className="tv-bulk" onClick={(e) => e.stopPropagation()}>
              <div className="tv-bulk-head">
                <div>
                  <h3>Bulk add amenities</h3>
                  <p>Add several amenities to one lounge in a single step.</p>
                </div>
                <button type="button" className="tv-bulk-x" onClick={closeBulk} disabled={bulkBusy} aria-label="Close"><FaTimes /></button>
              </div>

              {bulkDone ? (
                <div className="tv-bulk-done">
                  <div className="tv-bulk-done-icon"><FaCheck /></div>
                  <h4>{bulkDone.count} amenit{bulkDone.count === 1 ? 'y' : 'ies'} added</h4>
                  <p>
                    {bulkLounge ? loungeLabel(bulkLounge) : ''}
                    {bulkDone.skipped > 0 ? ` · ${bulkDone.skipped} skipped as duplicates` : ''}
                  </p>
                  <div className="tv-bulk-foot tv-bulk-foot-center">
                    <button type="button" className="tv-bulk-secondary" onClick={resetBulk}>Add more</button>
                    <button type="button" className="tv-bulk-primary" onClick={closeBulk}>Done</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="tv-bulk-body">
                    <div className="tv-bulk-fields">
                      <div className="tv-bulk-field" ref={bulkLoungeRef}>
                        <span>Lounge</span>
                        <button
                          type="button"
                          className="tv-bulk-select"
                          onClick={() => setBulkLoungeOpen((o) => !o)}
                          aria-haspopup="listbox"
                          aria-expanded={bulkLoungeOpen}
                        >
                          <span className={bulkLounge ? '' : 'is-placeholder'}>
                            {bulkLounge ? loungeLabel(bulkLounge) : (bulkLoungesLoading ? 'Loading lounges…' : 'Select a lounge')}
                          </span>
                          <span className="tv-select-caret">▾</span>
                        </button>
                        {bulkLoungeOpen && (
                          <>
                            <div className="tv-bulk-backdrop" onMouseDown={() => setBulkLoungeOpen(false)} />
                            <div className="tv-select-menu tv-bulk-menu" role="listbox" style={floatingMenuStyle(bulkLoungeRef, 340)}>
                              <input
                                type="text"
                                className="tv-bulk-menu-search"
                                autoFocus
                                value={bulkLoungeQ}
                                onChange={(e) => setBulkLoungeQ(e.target.value)}
                                placeholder="Search lounges…"
                              />
                              {bulkLoungesLoading && <div className="tv-select-item tv-select-item-muted">Loading…</div>}
                              {!bulkLoungesLoading && visibleLounges.length === 0 && (
                                <div className="tv-select-item tv-select-item-muted">No lounges found</div>
                              )}
                              {visibleLounges.map((l) => (
                                <button
                                  key={l.LoungeID}
                                  type="button"
                                  className={`tv-select-item${bulkLounge && bulkLounge.LoungeID === l.LoungeID ? ' tv-select-item-selected' : ''}`}
                                  onClick={() => { setBulkLounge(l); setBulkLoungeOpen(false); setBulkLoungeQ(''); }}
                                >
                                  {loungeLabel(l)}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      <label className="tv-bulk-field">
                        <span>Status for all</span>
                        <input
                          type="text"
                          list="tv-bulk-status-list"
                          value={bulkStatus}
                          onChange={(e) => setBulkStatus(e.target.value)}
                          placeholder="e.g. Available"
                        />
                        <datalist id="tv-bulk-status-list">
                          {statusChoices.map((s) => <option key={s} value={s} />)}
                        </datalist>
                      </label>
                    </div>

                    <div className="tv-bulk-cols">
                      <div className="tv-bulk-col">
                        <div className="tv-bulk-col-title">
                          Pick from existing amenities
                          <span>{bulkChecked.length} selected</span>
                        </div>
                        <input
                          type="text"
                          className="tv-bulk-search"
                          value={bulkSearch}
                          onChange={(e) => setBulkSearch(e.target.value)}
                          placeholder="Search amenities…"
                        />
                        <div className="tv-bulk-list">
                          {visibleOptions.length === 0 && <div className="tv-bulk-empty">No amenities match.</div>}
                          {visibleOptions.map((o) => (
                            <label key={o} className={`tv-bulk-item${bulkChecked.includes(o) ? ' is-on' : ''}`}>
                              <input type="checkbox" checked={bulkChecked.includes(o)} onChange={() => toggleBulkName(o)} />
                              <span>{o}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="tv-bulk-col">
                        <div className="tv-bulk-col-title">
                          Or paste / type a list
                          <span>one per line</span>
                        </div>
                        <textarea
                          className="tv-bulk-text"
                          value={bulkText}
                          onChange={(e) => setBulkText(e.target.value)}
                          placeholder={"Free Wi-Fi\nShowers\nBuffet | https://example.com/buffet.jpg"}
                        />
                        <div className="tv-bulk-hint">Optional: add <b>| image URL</b> after a name. You can also set images below. New names are saved to your amenity list.</div>
                      </div>
                    </div>

                    <div className="tv-bulk-preview">
                      <div className="tv-bulk-preview-title">
                        Will be added <b>{plan.toAdd.length}</b>
                        {plan.alreadyThere.length > 0 && <span className="tv-bulk-warn"> · {plan.alreadyThere.length} already on this lounge (skipped)</span>}
                        {plan.repeated > 0 && <span className="tv-bulk-warn"> · {plan.repeated} repeated in list</span>}
                      </div>
                      {plan.toAdd.length === 0 && <span className="tv-bulk-empty">Nothing selected yet.</span>}
                      {plan.toAdd.length > 0 && (
                        <div className="tv-bulk-rows">
                          {plan.toAdd.map((a) => (
                            <div key={a.key} className="tv-bulk-row">
                              <span className="tv-bulk-row-name">{a.name}</span>
                              <input
                                type="text"
                                className="tv-bulk-row-url"
                                value={a.url}
                                onChange={(e) => setBulkImages((prev) => ({ ...prev, [a.key]: e.target.value }))}
                                placeholder="Image URL (optional)"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      {plan.alreadyThere.length > 0 && (
                        <div className="tv-bulk-chips">
                          {plan.alreadyThere.map((n) => <span key={`d-${n}`} className="tv-bulk-chip is-dup">{n}</span>)}
                        </div>
                      )}
                    </div>

                    {bulkError && <div className="tv-bulk-error">{bulkError}</div>}
                  </div>

                  <div className="tv-bulk-foot">
                    <button type="button" className="tv-bulk-secondary" onClick={closeBulk} disabled={bulkBusy}>Cancel</button>
                    <button type="button" className="tv-bulk-primary" onClick={submitBulk} disabled={bulkBusy || plan.toAdd.length === 0 || !bulkLounge}>
                      {bulkBusy ? 'Adding…' : `Add ${plan.toAdd.length || ''} amenit${plan.toAdd.length === 1 ? 'y' : 'ies'}`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Add Custom Amenity Modal */}
      {showCustomAmenityInput && (
        <div className="tv-modal-overlay" onClick={() => { setShowCustomAmenityInput(false); setCustomAmenityName(''); setCustomAmenityTargetField(null); }}>
          <div className="tv-modal" onClick={e => e.stopPropagation()}>
            <div className="tv-modal-header">
              <h3>Add Custom Amenity</h3>
              <button
                className="tv-modal-close"
                onClick={() => { setShowCustomAmenityInput(false); setCustomAmenityName(''); setCustomAmenityTargetField(null); }}
                aria-label="Close"
              >×</button>
            </div>
            <div className="tv-modal-body">
              <label htmlFor="customAmenityInput">Amenity Name</label>
              <input
                id="customAmenityInput"
                type="text"
                value={customAmenityName}
                onChange={e => setCustomAmenityName(e.target.value)}
                placeholder="Enter custom amenity"
                disabled={prefillLoading}
              />
            </div>
            <div className="tv-modal-actions">
              <button
                onClick={async () => {
                  if (!customAmenityName.trim() || !customAmenityTargetField) { setShowCustomAmenityInput(false); return; }
                  try {
                    const { getAuth } = await import('firebase/auth');
                    const auth = getAuth();
                    const token = await auth.currentUser.getIdToken();
                    const res = await axios.post('/amenity-title/', { AmenityName: customAmenityName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
                    setAmenityOptions(prev => Array.from(new Set([...(prev || []), res.data.AmenityName])));
                    setNewRow(nr => ({ ...nr, [customAmenityTargetField]: res.data.AmenityName }));
                    setCustomAmenityName('');
                    setCustomAmenityTargetField(null);
                    setShowCustomAmenityInput(false);
                  } catch (err) {
                    alert('Failed to add custom amenity: ' + (err.response?.data?.detail || err.message));
                  }
                }}
                disabled={prefillLoading}
              >Add</button>
              <button
                className="secondary"
                onClick={() => { setShowCustomAmenityInput(false); setCustomAmenityName(''); setCustomAmenityTargetField(null); }}
                disabled={prefillLoading}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableViewer;
